import React, { useState } from "react";
import { Users } from "lucide-react";
import Modal from "./Modal";
import TeamSettingsModal from "./modals/TeamSettingsModal";
import RoleEnum from "../../../components/enums/RoleEnum";

const TeamsList = ({ loading, teams, selectedTeam, onSelect, user, onCreateTeam, onUpdateTeam, onCreatedTeam, currentUserRole }) => {
    const [showModal, setShowModal] = useState(false);
    const [newTeamName, setNewTeamName] = useState("");
    // Count how many teams the user is owner of (derived, not stateful)
    const ownerTeamsCount = teams.filter(team => team.current_user_role === 'owner').length;
    const handleCreateTeam = (e) => {
        e.preventDefault();
        if (newTeamName.trim()) {
            // Create the team and get its id
            const newTeamId = onCreateTeam(newTeamName.trim());
            if (onCreatedTeam && newTeamId) {
                onCreatedTeam(newTeamId);
            }

        }
        setShowModal(false);
        setNewTeamName("");
    };


    // Settings Modal state
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [settingsTeam, setSettingsTeam] = useState(null);
    const handleOpenSettings = (team) => {
        setSettingsTeam(team);
        setShowSettingsModal(true);
    };
    const handleCloseSettings = () => {
        setShowSettingsModal(false);
        setSettingsTeam(null);
    };
    const handleSaveSettings = (updatedTeam) => {
        if (onUpdateTeam) {
            // Update the team in the teams array to reflect changes immediately
            onUpdateTeam(updatedTeam);
        }
        handleCloseSettings();
    };

    // Split teams into owned and joined
    // Normalize inviteLinkEnabled to boolean for all teams
    const normalizedTeams = teams.map(team => ({
        ...team,
        inviteLinkEnabled: typeof team.inviteLinkEnabled === 'boolean'
            ? team.inviteLinkEnabled
            : (team.inviteLinkEnabled === 1)
    }));
    const ownedTeams = normalizedTeams.filter(team => team.current_user_role === 'owner');
    const joinedTeams = normalizedTeams.filter(team => team.current_user_role !== 'owner');

    if (loading) {
        return (
            <div className="p-6 text-center">
                <span className="animate-spin inline-block mr-2">🔄</span> Loading teams...
            </div>
        );
    }
    return (
        <div>
        {ownedTeams.length > 0 && (
            <>
                <div className="flex flex-col min-h-[200px] h-full mb-8">
                    <ul className={ownedTeams.length > 4 ? "max-h-[430px] overflow-y-auto pr-2" : ""}>
                        {ownedTeams.slice().sort((a, b) => a.name.localeCompare(b.name)).map((team, idx) => (
                            <React.Fragment key={team.id}>
                                <li
                                    className={`py-3 flex flex-col px-2 rounded-lg transition-colors duration-150 cursor-pointer border-l-4
                                    ${selectedTeam === team.id
                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                                        : 'bg-gray-50 dark:bg-gray-800/50 border-transparent'}`}
                                    onClick={() => onSelect(team.id)}
                                >
                                    <span className={`font-medium block ${selectedTeam === team.id ? 'text-blue-900 dark:text-blue-100' : 'text-gray-800 dark:text-gray-100'} flex items-center justify-between`}>
                                        <span>
                                            {team.name}
                                            {team.current_user_role === 'moderator' && (
                                                <span className="inline-flex items-center ml-2" title="You are a moderator">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4l7 4v4c0 5.25-3.5 9.74-7 10-3.5-.26-7-4.75-7-10V8l7-4z" />
                                                    </svg>
                                                    <span className="sr-only">Moderator</span>
                                                </span>
                                            )}
                                        </span>
                                        {team.inviteLinkEnabled && (
                                            <span className="ml-2 flex items-center" title="Invite link is active">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.59 13.41a2 2 0 0 1 0-2.82l4-4a2 2 0 1 1 2.82 2.82l-1.59 1.59" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.41 10.59a2 2 0 0 1 0 2.82l-4 4a2 2 0 1 1-2.82-2.82l1.59-1.59" />
                                                </svg>
                                            </span>
                                        )}
                                    </span>
                                    <div className="flex justify-between items-center mt-1">
                                        <span className={`text-xs ${selectedTeam === team.id ? 'text-blue-700 dark:text-blue-200' : 'text-gray-500 dark:text-gray-400'}`}>{new Date(team.created_at).toLocaleDateString()}</span>
                                        <span className={`flex items-center gap-1 bg-gray-100 dark:bg-gray-800 py-0.5 px-2 rounded-full text-xs ${selectedTeam === team.id ? 'text-blue-700 dark:text-blue-200' : 'text-gray-500 dark:text-gray-300'}`}> 
                                            <Users className="w-3 h-3" />
                                            {team.members}
                                            {team.action_required > 0 && (
                                                <span className="relative inline-flex items-center ml-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" /></svg>
                                                    <span className="absolute -top-2 -right-3 bg-orange-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center border border-white dark:border-gray-900">
                                                        {team.action_required}
                                                    </span>
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    {/* Settings Button Row */}
                                    {team.current_user_role === 'owner' && (
                                     <div className="flex gap-2 mt-2">
                                         {/* Example: Delete button here */}
                                         <button
                                             className="flex items-center gap-1 px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 text-xs font-medium border border-gray-200 dark:border-gray-600"
                                             onClick={e => { e.stopPropagation(); handleOpenSettings(team); }}
                                             title="Team Settings"
                                         >
                                             <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                             Settings
                                         </button>
                                         {/* TODO: Place Delete and Invite buttons here as needed */}
                                     </div>
                                    )}
                                </li>
                                {idx < ownedTeams.length - 1 && (
                                    <div className="border-t border-gray-200 dark:border-gray-700 mx-2" />
                                )}
                            </React.Fragment>
                        ))}
                    </ul>
                </div>
                </>
                    )}
                    {joinedTeams.length > 0 && (
                        <>
                            <hr />
                            <h2 className="text-xl font-semibold mb-4 dark:text-white">Joined Teams</h2>
                            <div className="flex flex-col h-full mb-8">
                                <ul className={joinedTeams.length > 3 ? "max-h-[230px] overflow-y-auto pr-2" : ""}>
                                    {joinedTeams.slice().sort((a, b) => a.name.localeCompare(b.name)).map((team, idx) => (
                                        <React.Fragment key={team.id}>
                                            <li
                                                className={`py-3 flex flex-col px-2 rounded-lg transition-colors duration-150 cursor-pointer border-l-4
                                                ${selectedTeam === team.id
                                                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                                                    : 'bg-gray-50 dark:bg-gray-800/50 border-transparent'}`}
                                                onClick={() => onSelect(team.id)}
                                            >
                                                <span className={`font-medium block ${selectedTeam === team.id ? 'text-blue-900 dark:text-blue-100' : 'text-gray-800 dark:text-gray-100'}`}>{team.name}{team.current_user_role === 'moderator' && (
                                                    <span className="inline-flex items-center ml-2" title="You are a moderator">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4l7 4v4c0 5.25-3.5 9.74-7 10-3.5-.26-7-4.75-7-10V8l7-4z" />
                                                        </svg>
                                                        <span className="sr-only">Moderator</span>
                                                    </span>
                                                )}
                                            </span>
                                            <div className="flex justify-between items-center mt-1">
                                                <span className={`text-xs ${selectedTeam === team.id ? 'text-blue-700 dark:text-blue-200' : 'text-gray-500 dark:text-gray-400'}`}>{new Date(team.created_at).toLocaleDateString()}</span>
                                                <span className={`flex items-center gap-1 bg-gray-100 dark:bg-gray-800 py-0.5 px-2 rounded-full text-xs ${selectedTeam === team.id ? 'text-blue-700 dark:text-blue-200' : 'text-gray-500 dark:text-gray-300'}`}> 
                                                    <Users className="w-3 h-3" />
                                                    {team.members}
                                                    {team.action_required > 0 && (
                                                <span className="relative inline-flex items-center ml-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" /></svg>
                                                    <span className="absolute -top-2 -right-3 bg-orange-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center border border-white dark:border-gray-900">
                                                        {team.action_required}
                                                    </span>
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                </li>
                                {idx < joinedTeams.length - 1 && (
                                    <div className="border-t border-gray-200 dark:border-gray-700 mx-2" />
                                )}
                            </React.Fragment>
                        ))}
                    </ul>
                </div>
            </>
        )}
        {(ownedTeams.length === 0 && joinedTeams.length === 0) && (
            <div className="flex flex-col items-center justify-center py-12">
                <div className=" rounded-full p-6 mb-4">
                    <Users className="w-16 h-16 text-gray-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2 px-4">No teams yet!</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-xs px-4 text-center">Create your first team to start collaborating and organizing your projects with others.</p>
            </div>
        )}
        {/* Create Team Button and Modal */}
        <div className="mt-6 flex flex-col items-center">
            {/* Create Team Button and Modal */}
            <div className="mt-6 flex flex-col items-center">
                {(() => {
                    let teamLimit = Infinity;
                    if (user?.role === RoleEnum.DefaultUser) teamLimit = 1;
                    else if (user?.role === RoleEnum.ProUser || user?.role === RoleEnum.Trainer) teamLimit = 10;
                    const hasReachedLimit = ownerTeamsCount >= teamLimit;
                    return (
                        <div>
                            <button
                                className="bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-bold py-2 px-6 rounded shadow transition flex items-center gap-2 disabled:opacity-60"
                                onClick={() => setShowModal(true)}
                                disabled={hasReachedLimit}
                            >
                                + Create Team
                            </button>
                            {hasReachedLimit && (
                                <span className="text-xs mt-2 text-red-500 font-medium text-center">
                                    {teamLimit === 1 && "You can only create 1 team"}
                                    {teamLimit === 10 && "You can only create up to 10 teams"}
                                </span>
                            )}
                        </div>
                    );
                })()}
            </div>
        </div>
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
            <h2 className="text-lg font-semibold mb-4 dark:text-white">Create a New Team</h2>
            <form onSubmit={handleCreateTeam} className="flex flex-col gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Team Name</label>
                    <input
                        type="text"
                        className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400"
                        placeholder="Enter team name"
                        value={newTeamName}
                        onChange={e => setNewTeamName(e.target.value)}
                        required
                    />
                </div>
                <div className="flex gap-2 justify-end">
                    <button
                        type="button"
                        className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-700"
                        onClick={() => setShowModal(false)}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-bold py-2 px-6 rounded shadow transition"
                    >
                        Create
                    </button>
                </div>
            </form>
        </Modal>
    {/* Team Settings Modal */}
    <TeamSettingsModal
        isOpen={showSettingsModal}
        onClose={handleCloseSettings}
        team={settingsTeam}
        onSave={handleSaveSettings}
    />
    </div>
    );
};

export default TeamsList;
